import { addMinutes, format, isAfter, isBefore, isEqual } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import type { MeetingDate } from '../types/meetings.type';
import { PARIS_TZ } from './dateUtils';

export { PARIS_TZ };

// Ancre une heure murale (« HH:mm ») du jour calendaire affiché sur Europe/Paris
// et renvoie l'instant absolu correspondant, quel que soit le fuseau du navigateur.
// Ainsi un créneau « 14:30 » signifie toujours 14h30 heure de Paris.
export const parseSlotInParis = (day: Date, time: string): Date =>
  fromZonedTime(`${format(day, 'yyyy-MM-dd')}T${time}:00`, PARIS_TZ);

// Les créneaux (`timeSlotStart`/`timeSlotEnd`) sont des instants absolus ancrés
// sur Europe/Paris, tout comme les meetings renvoyés par le backend. On compare
// donc directement instant à instant, indépendamment du fuseau du navigateur.
export const doesOverlap = (
  timeSlotStart: Date,
  timeSlotEnd: Date,
  meetings: Array<MeetingDate>,
  endOfTheDay = '18:00',
): boolean => {
  // Heure de fermeture du jour, ancrée sur Europe/Paris.
  const dayStrParis = formatInTimeZone(timeSlotStart, PARIS_TZ, 'yyyy-MM-dd');
  const endLimit = fromZonedTime(`${dayStrParis}T${endOfTheDay}:00`, PARIS_TZ);

  // Check if selected time exceeds day limit
  if (isAfter(timeSlotEnd, endLimit)) {
    return true;
  }

  // Compare against existing meeting
  return meetings.some((meeting) => {
    const meetingStart = new Date(meeting.start_time);
    const meetingEnd = new Date(meeting.end_time);

    return isBefore(timeSlotStart, meetingEnd) && isAfter(timeSlotEnd, meetingStart);
  });
};

export const isCurrentMeetingDate = (
  date: Date,
  dateStart: Date,
  duration: number | Date,
): boolean => {
  const start = new Date(dateStart);
  const end = typeof duration === 'number' ? addMinutes(start, duration) : new Date(duration);

  return isEqual(date, start) || (isAfter(end, date) && isBefore(start, date));
};
